// 🔊 메시지 읽어주기(TTS) — 폰 네이티브 음성 합성 브릿지 (2026-09-06 원격 "듣기 기능 … 밀면 되게 / 번역문은 선호 언어로만")
//   운전 중 등 화면을 못 볼 때 메시지를 소리로 듣는다. 안드로이드 WebView 는 Web Speech Synthesis 지원이 들쭉날쭉해
//   셸의 android.speech.tts.TextToSpeech 를 브릿지로 쓰고(정확·안정), 없으면 브라우저 speechSynthesis 로 폴백한다.
//   셸(NotifyBridge): ttsAvailable() / ttsSpeak(text, bcp47) / ttsStop()
//   🔴 iOS 셸도 window.TPTAndroid 를 주입하므로 **메서드 존재**로 판별한다 — iOS 가 같은 이름을 구현하면 그대로 동작.
//   언어 매핑은 음성 입력(speech.ts)과 같은 BCP-47 표를 공유한다(DRY).
//   🔊 읽는 중 상태 (2026-09-07 원격 "마이크처럼 진행중 팝업과 중단 버튼") — speak() 성공 시 speaking=true, 끝나면 false 를
//   TTS_STATE_EVENT 로 알린다(TtsDock 이 표시). 종료 감지 3겹: 웹 utterance.onend / 네이티브 'tpt:tts' {state:'done'|'error'}(code 89+,
//   UtteranceProgressListener) / 길이 추정 안전 타이머(콜백이 없는 구 앱·이벤트 유실 대비 — 표시가 영영 남지 않게).
import { useEffect, useState } from 'react';
import type { LangCode } from './translate';
import { speechLocale } from './speech';

export interface TtsState { speaking: boolean; text: string }
export const TTS_STATE_EVENT = 'tpt:tts-state';
let ttsState: TtsState = { speaking: false, text: '' };
let endTimer: number | null = null;
let nativeListenerInstalled = false;
// 🔢 발화 세대 — A 읽는 중 B 를 읽으면 cancel/FLUSH 로 밀려난 A 의 종료(onend·onStop)가 **B 시작 뒤에** 도착해 도크를 즉시 지우던
//   회귀 방지(2026-09-07 리뷰 🔴). 웹은 utterance 객체 동일성, 네이티브는 utteranceId('tpt-tts-<n>') 로 현재 발화만 인정한다.
let gen = 0;
let curUtter: SpeechSynthesisUtterance | null = null;
let curNativeId = '';

function setTtsState(next: TtsState): void {
	ttsState = next;
	try { window.dispatchEvent(new CustomEvent(TTS_STATE_EVENT, { detail: next })); } catch { /* SSR */ }
}
function finishSpeaking(): void {
	if (endTimer !== null) { window.clearTimeout(endTimer); endTimer = null; }
	if (ttsState.speaking) setTtsState({ speaking: false, text: '' });
}
/** 발화 길이 추정(ms) — 한국어 TTS 기본 속도 ≈ 5~6 음절/초 → 글자당 ~170ms + 여유. 콜백 없는 구 앱의 표시 종료 기준(상한 6분). */
export function estimateSpeechMs(text: string): number {
	return Math.min(6 * 60_000, 2_000 + text.length * 170);
}
function beginSpeaking(text: string, hasNativeEvents: boolean): void {
	if (endTimer !== null) window.clearTimeout(endTimer);
	setTtsState({ speaking: true, text: text.length > 80 ? text.slice(0, 80) + '…' : text });
	// 이벤트가 오는 경로여도 안전 타이머는 둔다(유실 대비) — 긴 글도 끝까지 표시되게 넉넉히(15분 상한), 실제 종료는 이벤트가 닫는다.
	const ms = hasNativeEvents ? Math.min(15 * 60_000, estimateSpeechMs(text) * 3 + 10_000) : estimateSpeechMs(text);
	endTimer = window.setTimeout(() => { endTimer = null; finishSpeaking(); }, ms);
}
function ensureNativeListener(): void {
	if (nativeListenerInstalled || typeof window === 'undefined') return;
	nativeListenerInstalled = true;
	window.addEventListener('tpt:tts', (e) => {
		const d = (e as CustomEvent<{ state?: string; id?: string }>).detail;
		// id 가 오면(code 89+) 현재 발화만 인정 — 밀려난 이전 발화의 done/onStop 은 무시. id 없는 구 형식은 종전대로.
		if (d?.id && curNativeId && d.id !== curNativeId) return;
		if (d?.state === 'done' || d?.state === 'error') finishSpeaking();
	});
}
/** 읽는 중 표시(TtsDock)용 — 상태 변화를 구독한다. */
export function useTtsState(): TtsState {
	const [s, setS] = useState<TtsState>(ttsState);
	useEffect(() => {
		const h = (e: Event) => setS((e as CustomEvent<TtsState>).detail);
		window.addEventListener(TTS_STATE_EVENT, h);
		return () => window.removeEventListener(TTS_STATE_EVENT, h);
	}, []);
	return s;
}

type TtsBridge = {
	ttsAvailable?: () => boolean;
	ttsSpeak?: (text: string, lang: string) => void;
	ttsStop?: () => void;
	ttsHasEvents?: () => boolean;   // code 89+ — 발화 시작/종료를 'tpt:tts' {state, id} 이벤트로 알린다
	ttsSpeakId?: (text: string, lang: string, id: string) => void;   // code 89+ — utteranceId 를 지정해 발화(연속 읽기 구분)
};

function bridge(): TtsBridge | null {
	const b = (window as unknown as { TPTAndroid?: TtsBridge }).TPTAndroid;
	return b && typeof b.ttsSpeak === 'function' && typeof b.ttsStop === 'function' ? b : null;
}

/** 화면 언어 → 합성기 BCP-47. 사용자의 선호(화면) 언어로 읽는다. */
export function ttsLocale(lang: LangCode): string {
	return speechLocale(lang);
}

/** 읽어주기 가능 여부 — 셸 브릿지가 있거나(항상 가능으로 봄) 브라우저 speechSynthesis 가 있으면 true. */
export function ttsAvailable(): boolean {
	const b = bridge();
	if (b) {
		try { return typeof b.ttsAvailable === 'function' ? !!b.ttsAvailable.call(b) : true; } catch { return true; }
	}
	try { return typeof window !== 'undefined' && 'speechSynthesis' in window; } catch { return false; }
}

/**
 * 마크다운·인용·멘션 마크업을 걷어내 소리로 읽기 좋은 평문으로. (카드/스티커/시스템 메시지는 호출측에서 걸러 여기 오지 않는다)
 *   · 인용 줄(`> …`)·전달 스레드 줄 제거 · 링크 `[텍스트](url)` → 텍스트 · 강조(*,_,~,`)·머리표(#, 리스트 기호) 제거
 *   · 멘션 `@[이름](id)`·`@이름` → 이름만 남겨 자연스럽게 읽히게 · 연속 공백/빈 줄 접기
 */
export function plainForSpeech(text: string): string {
	let s = text || '';
	s = s.replace(/^\s*>.*$/gm, '');                       // 인용 줄 통째 제거
	s = s.replace(/@\[([^\]]+)\]\([^)]*\)/g, '$1');        // @[이름](id) → 이름
	s = s.replace(/\[([^\]]+)\]\([^)]*\)/g, '$1');         // [텍스트](url) → 텍스트
	s = s.replace(/```[\s\S]*?```/g, ' ');                 // 코드블록 제거
	s = s.replace(/`([^`]*)`/g, '$1');                     // 인라인 코드 기호 제거
	s = s.replace(/[*_~#>]+/g, ' ');                        // 강조·머리표 기호 제거
	s = s.replace(/^\s*[-+•]\s+/gm, '');                   // 리스트 불릿 제거
	s = s.replace(/[ \t]+/g, ' ').replace(/\n{2,}/g, '\n').trim();
	return s;
}

/** 텍스트를 소리로 읽는다. 새 읽기는 이전 읽기를 끊는다(FLUSH). 실제 발화가 시작됐다고 볼 수 있으면 true. */
export function speak(text: string, lang: LangCode): boolean {
	const clean = (text || '').trim();
	if (!clean) return false;
	const b = bridge();
	if (b && b.ttsSpeak) {
		try {
			const hasEvents = typeof b.ttsHasEvents === 'function' && (() => { try { return !!b.ttsHasEvents!.call(b); } catch { return false; } })();
			ensureNativeListener();
			curNativeId = `tpt-tts-${++gen}`;
			if (typeof b.ttsSpeakId === 'function') b.ttsSpeakId.call(b, clean, ttsLocale(lang), curNativeId);
			else { curNativeId = ''; b.ttsSpeak.call(b, clean, ttsLocale(lang)); }   // 구 앱 — id 없는 이벤트/타이머로 닫힌다
			beginSpeaking(clean, hasEvents);
			return true;
		} catch { /* 폴백으로 */ }
	}
	try {
		if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
			const u = new SpeechSynthesisUtterance(clean);
			u.lang = ttsLocale(lang);
			curUtter = u;
			u.onend = () => { if (curUtter === u) finishSpeaking(); };    // cancel() 로 밀려난 이전 utterance 의 onend 는 무시
			u.onerror = () => { if (curUtter === u) finishSpeaking(); };
			window.speechSynthesis.cancel();
			window.speechSynthesis.speak(u);
			beginSpeaking(clean, true);   // onend 가 오지만 브라우저별 유실이 있어 안전 타이머 병행
			return true;
		}
	} catch { /* 무해 */ }
	return false;
}

/**
 * 🔎 읽어주기 실패 진단 — 브릿지 객체·각 네이티브 메서드 노출 여부·브라우저 음성·설치 앱 code 를 한 줄로.
 *   "웹·APK 다 맞는데 특정 기기에서만 안 됨"의 진짜 원인(네이티브 미노출/구버전 앱/웹뷰 음성없음)을 화면에서 바로 확정한다. (2026-09-06 원격 근본조사)
 *   예: `TA=Y spk=N stop=N avail=N 웹음성=N code=86` → 앱 code 86(브릿지 없음), `spk=Y stop=Y … code=87` 인데 실패면 다른 원인.
 */
export function ttsDiag(): string {
	try {
		const b = (window as unknown as { TPTAndroid?: Record<string, unknown> }).TPTAndroid;
		const tf = (k: string) => (b && typeof b[k] === 'function' ? 'Y' : 'N');
		let code = '?';
		try {
			const gv = b?.getVersionCode;
			if (typeof gv === 'function') code = String((gv as () => number).call(b));
		} catch { /* 무해 */ }
		const ss = (() => { try { return typeof window !== 'undefined' && 'speechSynthesis' in window ? 'Y' : 'N'; } catch { return '?'; } })();
		return `TA=${b ? 'Y' : 'N'} spk=${tf('ttsSpeak')} stop=${tf('ttsStop')} avail=${tf('ttsAvailable')} 웹음성=${ss} code=${code}`;
	} catch { return 'diag-error'; }
}

/** 진행 중인 읽기를 멈춘다 — 방 전환·언마운트 시 잔여 발화가 남지 않도록. */
export function stopSpeaking(): void {
	const b = bridge();
	if (b && b.ttsStop) { try { b.ttsStop.call(b); } catch { /* 무해 */ } }
	try { if (typeof window !== 'undefined' && 'speechSynthesis' in window) window.speechSynthesis.cancel(); } catch { /* 무해 */ }
	curUtter = null; curNativeId = '';
	finishSpeaking();
}
